import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { useQuery } from '@tanstack/react-query'
import { CountryCode, isSupportedCountry } from 'libphonenumber-js'
import moment from 'moment-timezone'
import React, { useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectBinRangeInfoFromState,
  selectErrorDetailMapFromState,
  selectFetchingIssuerFromState,
  selectIssuerIdFromState,
  selectPostingCreditCardFromState,
  selectTopLevelErrorFromState,
} from 'src/app/RegisterAccount/redux/registerAccount.selectors'
import Registration from 'src/assets/images/AccountRegistration.png'
import AppName from 'src/components/AppName/AppName'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import Popup from 'src/components/Popup/Popup'
import RegisterAccountAdditionalInfoPage from 'src/components/RegisterAccountAdditionalInfoPage/RegisterAccountAdditionalInfoPage'
import RegisterAccountBinInputPage from 'src/components/RegisterAccountBinInputPage/RegisterAccountBinInputPage'
import RegisterAccountDisplayNameContent from 'src/components/RegisterAccountDisplayNameContent/RegisterAccountDisplayNameContent'
import RegisterAccountPaymentNetinputForm from 'src/components/RegisterAccountPaymentNetInputForm/RegisterAccountPaymentNetInputForm'
import RegisterAccountReviewInformation from 'src/components/RegisterAccountReviewInformation/RegisterAccountReviewInformation'
import SvgLocked from 'src/components/Svgs/SvgLocked'
import TestId from 'src/components/TestId/TestId'
import {
  AstradaBinData,
  calculateAstradaEligibility,
} from 'src/context/AstradaContext/AstradaContext'
import useAstrada from 'src/context/AstradaContext/useAstrada'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import {
  AmexCardTypeFields,
  Issuers,
  IssuerSchemaFilterFields,
  SupportedBrands,
} from 'src/enums'
import {
  FormFieldsValidation,
  useFlyoutDispatch,
  useFormFields,
} from 'src/hooks'
import { getLocaleInfo } from 'src/localization/locales'
import { translateNode } from 'src/localization/translateNode'
import { closeModal } from 'src/navigation/Lnk'
import { onboardingUrl, registerAccountUrl } from 'src/navigation/urls'
import {
  countriesQueries,
  toCountryListModelMap,
  toCountryOptions,
} from 'src/queries/countries/countries.queries'
import { JPM_ISSUER_ID } from 'src/queries/issuers/issuerIntegrators/issuerIntegrators.models'
import {
  issuerIntegratorQueries,
  toIssuerIntegratorListModel,
} from 'src/queries/issuers/issuerIntegrators/issuerIntegrators.queries'
import { pricingPlanQueries } from 'src/queries/organizations/pricingPlans/pricingPlans.queries'
import {
  createClearBinRangeInfoAction,
  getIssuerFromBin,
  postSecureCreditCardAndOpenToastAndCloseFlyout,
} from 'src/redux/actions/data/creditCards.actions'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import {
  selectCalculatedBackgroundLocation,
  selectLocationFromState,
} from 'src/redux/selectors/router.selectors'
import {
  selectAppName,
  selectBrandName,
} from 'src/redux/selectors/session.selectors'
import { errorPath } from 'src/utils/error.utils'
import {
  stringIsShortDate,
  stringIsShortDateInFuture,
} from 'src/utils/validation/date.validation'
import {
  cvcIsEmpty,
  stringIsEmpty,
  stringIsLuhnValid,
  stringLengthIsBetween,
} from 'src/utils/validation/string.validation'
import { css } from 'styled-components'

export type AccountDisplayNameFlatFields = {
  displayName: string
}

export type AccountAdditionalInfoPageFlatFields = {
  issuerId?: string
  country?: string
  organizationId?: string
  cardType?: string
  amexCardProperties?: {
    cardType: string
    cardholderType: string | null
  }
}

export type AccountBinInputPageFlatFields = {
  cardNumber?: string
  expirationDate?: string
}

export type PaymentNetInputPageFlatFields = {
  paymentNetOrgId?: string
  paymentNetUserId?: string
  paymentGroupId?: string
  last4?: string
}

export type AccountBinInputPageCvcFlatFields = {
  cvc?: string
}

export type RegisterAccountCountryFields = {
  address1?: string
  address2?: string
  city?: string
  province?: string
  postal?: string
}

export const amexCardTypes = [
  {
    value: AmexCardTypeFields.corporate,
    fieldText: translateNode('Corporate'),
  },
  {
    value: AmexCardTypeFields.business,
    fieldText: translateNode('Business'),
  },
  {
    value: AmexCardTypeFields.not_sure,
    fieldText: translateNode('Not sure'),
  },
]

export const amexIssuerCodes = [Issuers.Amex, Issuers.AmexDemo]

const RegisterAccount: React.FC = () => {
  const brand = useSelector(selectBrandName)
  const isJpm = brand === SupportedBrands.Jpm
  // Parity with the legacy flow, which fetched the issuer list on every
  // mount (kept unconditional deliberately; only the JPM branch ever read
  // it, and the selected issuer now comes from the detail query below).
  useQuery(issuerIntegratorQueries.list())
  // JPM has no BIN ranges, so the issuer id is fixed; otherwise it comes
  // from the BIN-range lookup (binRangeInfo.issuerId). Re-keying the detail
  // query by id replaces the legacy mismatch-refetch effect.
  const binRangeIssuerId = useSelector(selectIssuerIdFromState)
  const issuerId = isJpm ? JPM_ISSUER_ID : binRangeIssuerId
  const { data: selectedIssuer } = useQuery({
    ...issuerIntegratorQueries.detail(issuerId),
    select: toIssuerIntegratorListModel,
  })
  const locale = useSelector(selectNullSafeLocaleFromState)
  // Legacy behavior rendered with empty options/map until the fetch
  // succeeded, so the `= []` / `= {}` defaults preserve that exactly. If a
  // readiness gate is ever needed it is the query's isSuccess/isError.
  const { data: countryOptions = [] } = useQuery({
    ...countriesQueries.list(locale),
    select: toCountryOptions,
  })
  const { data: countrysMap = {} } = useQuery({
    ...countriesQueries.list(locale),
    select: toCountryListModelMap,
  })
  const postingCreditCard = useSelector(selectPostingCreditCardFromState)
  const fetchingIssuer = useSelector(selectFetchingIssuerFromState)
  const topLevelError = useSelector(selectTopLevelErrorFromState)
  const errorDetailMap = useSelector(selectErrorDetailMapFromState)
  const user = useCurrentUser()
  const { analytics } = useAnalyticsContext()
  const binRangeInfo = useSelector(selectBinRangeInfoFromState)
  const appName = useSelector(selectAppName)
  const location = useSelector(selectLocationFromState)
  const backgroundLocation = useSelector(selectCalculatedBackgroundLocation)
  const {
    enable_embedded_chargebee_checkout: enableEmbeddedChargebeeCheckout,
  } = useSelector(selectFeatureFlaggerFromState) || {}

  const cvcRequired =
    selectedIssuer?.schema !== undefined &&
    selectedIssuer.schema.filter(
      (field) =>
        field.name === IssuerSchemaFilterFields.cvc && field.required === true,
    ).length > 0

  const dispatch = useFlyoutDispatch()
  const astrada = useAstrada()
  const pendingBinLookupRef = useRef(false)
  const [currentPage, setCurrentPage] = useState(
    location.state?.currentPage || 1,
  )
  const [hideCloseButton, setHideCloseButton] = useState<boolean>(false)
  const shouldUseReorderedPricingFlow = Boolean(
    astrada.onboarding && astrada.binData && enableEmbeddedChargebeeCheckout,
  )
  const saasEligibilityIssuerId = shouldUseReorderedPricingFlow
    ? astrada.binData?.issuerId
    : undefined
  const { data: saasEligibility, isLoading: saasEligibilityLoading } = useQuery(
    pricingPlanQueries.saasEligibility(saasEligibilityIssuerId),
  )
  const shouldRouteToPricingAfterCardRegistration = Boolean(
    shouldUseReorderedPricingFlow &&
      saasEligibilityIssuerId &&
      saasEligibility?.saasEnabled !== false,
  )
  const shouldBlockSaasEligibilitySubmit =
    Boolean(saasEligibilityIssuerId) && saasEligibilityLoading
  const shouldShowSaasEligibilityLoader =
    currentPage === 4 && shouldBlockSaasEligibilitySubmit

  // need this for CVC field validation
  useEffect((): void => {
    if (!pendingBinLookupRef.current) return
    if (fetchingIssuer) return
    if (binRangeInfo === undefined) {
      pendingBinLookupRef.current = false
      return
    }
    // assumes getBinRange was successful

    if (!binRangeInfo.supported) {
      pendingBinLookupRef.current = false
      return
    }

    if (selectedIssuer === undefined) return

    if (cvcRequired && !accountBinInputPageCvcFlatFieldsSetErrors()) {
      pendingBinLookupRef.current = false
      return
    }

    pendingBinLookupRef.current = false
    analytics?.track('register account - card info - BIN match', {
      intent: 'no error returned - BIN match',
      interaction: 'button press',
      screen: registerAccountUrl(),
      binRange: `${binRangeInfo.rangeMin}-${binRangeInfo.rangeMax}`,
    })
    const { cardNumber, expirationDate } = accountBinInputPageFlatFields
    const cvc = accountBinInputPageCvcFlatFields.cvc
    // Card number and expiration must be truthy. CVC is only required if the issuer requires it.
    const ccDataValid =
      !!(cardNumber && expirationDate) && (!cvcRequired || !!cvc)
    if (ccDataValid) {
      const { partnerEnabled, astradaEnabled, ...rest } = binRangeInfo
      const eligibility = calculateAstradaEligibility({
        partnerEnabled,
        astradaEnabled,
      })
      const astradaBinData: AstradaBinData = {
        ...rest,
        astradaEnabled,
        eligibility,
        issuerLogoImg: selectedIssuer?.url,
        issuerRequiresVerification: selectedIssuer?.needsCardholderVerification,
      }
      astrada.updateState({
        binData: astradaBinData,
        cardData: {
          number: cardNumber,
          cvc: cvc,
          expiration: expirationDate,
        },
      })
    }
    setCurrentPage(2)
  }, [binRangeInfo, fetchingIssuer, selectedIssuer])

  const accountBinInputPageFlatFieldsValidation =
    (): FormFieldsValidation<AccountBinInputPageFlatFields> => {
      return {
        cardNumber: [stringIsEmpty(), stringIsLuhnValid],
        expirationDate: [
          stringIsEmpty(),
          stringIsShortDate(locale),
          stringIsShortDateInFuture(locale),
        ],
      }
    }
  const accountBinInputPageCvcFlatFieldsValidation =
    (): FormFieldsValidation<AccountBinInputPageCvcFlatFields> => {
      return {
        cvc: [cvcIsEmpty, stringLengthIsBetween(3, 4)],
      }
    }

  const {
    fields: accountBinInputPageFlatFields,
    fieldsErrors: accountBinInputPageFlatFieldsErrors,
    onFieldChange: onAccountBinInputPageFlatFieldChange,
    setErrorsAndReturnDidPassValidation: accountBinInputPageFlatFieldsSetErrors,
  } = useFormFields<AccountBinInputPageFlatFields>(
    {},
    accountBinInputPageFlatFieldsValidation,
    errorDetailMap,
    {
      'createAccountRequest.cardNumber': 'cardNumber',
      'createAccountRequest.expirationDate': 'expirationDate',
    },
  )

  const {
    fields: jpmFlatFields,
    fieldsErrors: jpmFlatFieldsErrors,
    onFieldChange: onJpmFlatFieldChange,
    setErrorsAndReturnDidPassValidation: jpmFlatFieldsSetErrors,
  } = useFormFields<PaymentNetInputPageFlatFields>(
    {},
    () => ({
      paymentNetOrgId: [stringIsEmpty()],
      paymentNetUserId: [stringIsEmpty()],
      paymentGroupId: [stringIsEmpty()],
      last4: [stringIsEmpty()],
    }),
    undefined,
    {
      paymentNetOrgId: 'paymentNetOrgId',
      paymentNetUserId: 'paymentNetUserId',
      paymentGroupId: 'paymentGroupId',
      last4: 'last4',
    },
  )
  const cardInfoHeader = (
    <div
      css={css`
        display: flex;
        flex-direction: row;
        margin-bottom: 20px;
      `}
    >
      <span
        css={css`
          display: flex;
          align-items: center;
          ${getFontCss(
            12,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.regular,
          )};
        `}
      >
        <SvgLocked
          css={css`
            transform-origin: left;
            transform: scale(calc(5 / 7));
          `}
        />
        {translateNode(
          'Our security controls meet the highest industry standards',
          {
            appName: <AppName />,
          },
        )}
      </span>
    </div>
  )

  const onPaymentNetInputFormSubmit = (): void => {
    analytics?.track('register card - continue')
    const paymentNetInfoFormFlatFieldsPassedValidation =
      jpmFlatFieldsSetErrors()
    if (paymentNetInfoFormFlatFieldsPassedValidation) {
      setCurrentPage(2)
    }
  }

  // auto-populate old card info flat fields
  useEffect((): void => {
    if (location.state?.accountBinInputPageFlatFields) {
      // TODO: eslint tech debt
      // eslint-disable-next-line no-unsafe-optional-chaining
      const { cardNumber, expirationDate } =
        // eslint-disable-next-line no-unsafe-optional-chaining
        location.state?.accountBinInputPageFlatFields
      onAccountBinInputPageFlatFieldChange('cardNumber', cardNumber)
      onAccountBinInputPageFlatFieldChange('expirationDate', expirationDate)
    }
  }, [location.state?.accountBinInputPageFlatFields])

  // auto-populate old payment net info flat fields
  useEffect((): void => {
    if (location.state?.paymentNetInfoFlatFields) {
      // TODO: eslint tech debt

      const { paymentNetOrgId, paymentNetUserId, paymentGroupId, last4 } =
        /* eslint-disable-next-line no-unsafe-optional-chaining */
        location.state?.paymentNetInfoFlatFields
      onJpmFlatFieldChange('paymentNetOrgId', paymentNetOrgId)
      onJpmFlatFieldChange('paymentNetUserId', paymentNetUserId)
      onJpmFlatFieldChange('paymentGroupId', paymentGroupId)
      onJpmFlatFieldChange('last4', last4)
    }
  }, [location.state?.paymentNetInfoFlatFields])

  const step1FlatFields =
    location.state?.accountBinInputPageFlatFields ||
    accountBinInputPageFlatFields ||
    (isJpm ? jpmFlatFields : {})
  const step1FlatFieldsJpm =
    location.state?.paymentNetInfoFlatFields || jpmFlatFields

  const {
    fields: accountBinInputPageCvcFlatFields,
    fieldsErrors: accountBinInputPageCvcFlatFieldsErrors,
    onFieldChange: onAccountBinInputPageCvcFlatFieldChange,
    setErrorsAndReturnDidPassValidation:
      accountBinInputPageCvcFlatFieldsSetErrors,
  } = useFormFields<AccountBinInputPageCvcFlatFields>(
    {},
    accountBinInputPageCvcFlatFieldsValidation,
    errorDetailMap,
    {
      'createAccountRequest.cvc': 'cvc',
    },
  )

  // auto-populate old cvc flat fields
  useEffect((): void => {
    if (location.state?.accountBinInputPageCvcFlatFields) {
      // TODO: eslint tech debt
      // eslint-disable-next-line no-unsafe-optional-chaining
      const { cvc } = location.state?.accountBinInputPageCvcFlatFields
      onAccountBinInputPageCvcFlatFieldChange('cvc', cvc)
    }
  }, [location.state?.accountBinInputPageCvcFlatFields])

  const step1CvcFlatFields =
    location.state?.accountBinInputPageCvcFlatFields ||
    accountBinInputPageCvcFlatFields

  const onAccountBinInputPageSubmit = (bin?: string): void => {
    pendingBinLookupRef.current = false
    const accountBinInputPageFlatFieldsPassedValidation =
      accountBinInputPageFlatFieldsSetErrors()
    if (bin && accountBinInputPageFlatFieldsPassedValidation) {
      pendingBinLookupRef.current = true
      dispatch(
        getIssuerFromBin(
          { bin: bin.replace(/\s/g, '').substring(0, 10) },
          analytics,
        ),
      )
      // next step in logic is in the binRangeInfo useEffect
    }
  }

  const accountAdditionalInfoPageFlatFieldsValidation =
    (): FormFieldsValidation<AccountAdditionalInfoPageFlatFields> => ({
      country: [stringIsEmpty()],
      cardType:
        selectedIssuer !== undefined &&
        amexIssuerCodes.includes(selectedIssuer.code as Issuers)
          ? [stringIsEmpty('Please select an option')]
          : undefined,
    })
  const {
    fields: accountAdditionalInfoPageFlatFields,
    fieldsErrors: accountAdditionalInfoPageFlatFieldsErrors,
    onFieldChange: onAccountAdditionalInfoPageFlatFieldChange,
    setErrorsAndReturnDidPassValidation:
      accountAdditionalInfoPageFlatFieldsSetErrors,
    topmostScrollGroup: accountAdditionalInfoPageFlatFieldsTopmostScrollGroup,
  } = useFormFields<AccountAdditionalInfoPageFlatFields>(
    {
      country: isSupportedCountry(locale.slice(-2).toUpperCase())
        ? (locale.slice(-2).toUpperCase() as CountryCode)
        : 'US',
    },
    accountAdditionalInfoPageFlatFieldsValidation,
    errorDetailMap,
    {
      'createAccountRequest.issuerId': 'issuerId',
      'createAccountRequest.country': 'country',
      'createAccountRequest.cardType': 'cardType',
    },
    {
      issuerId: 0,
      country: 1,
    },
  )

  const issuerSchema =
    selectedIssuer !== undefined && selectedIssuer.registrable
      ? selectedIssuer.schema
      : undefined

  const issuerFieldsValidation =
    issuerSchema !== undefined
      ? (): FormFieldsValidation<Record<string, string>> =>
          issuerSchema
            .filter(
              (field) =>
                field.name !== IssuerSchemaFilterFields.cardNumber &&
                field.name !== IssuerSchemaFilterFields.expires &&
                field.name !== IssuerSchemaFilterFields.cvc,
            )
            .reduce((acc, curr) => {
              return curr.required
                ? { ...acc, [curr.name]: [stringIsEmpty()] }
                : acc
            }, {})
      : (): FormFieldsValidation<Record<string, string>> => ({})

  const {
    fields: issuerFields,
    setFields: setIssuerFields,
    fieldsErrors: issuerFieldsErrors,
    onFieldChange: onIssuerFieldChange,
    setErrorsAndReturnDidPassValidation: issuerFieldsSetErrors,
    topmostScrollGroup: issuerFieldsTopmostScrollGroup,
  } = useFormFields<Record<string, string>>(
    {},
    issuerFieldsValidation,
    errorDetailMap,
    issuerSchema
      ?.filter(
        (field) =>
          field.name !== IssuerSchemaFilterFields.cardNumber &&
          field.name !== IssuerSchemaFilterFields.expires &&
          field.name !== IssuerSchemaFilterFields.cvc,
      )
      .reduce(
        (acc, curr) => ({
          ...acc,
          [errorPath(['issuerFields', curr.name])]: curr.name,
        }),
        {},
      ),
    issuerSchema
      ?.filter(
        (field) =>
          field.name !== IssuerSchemaFilterFields.cardNumber &&
          field.name !== IssuerSchemaFilterFields.expires &&
          field.name !== IssuerSchemaFilterFields.cvc,
      )
      .reduce(
        (acc, curr) => ({
          ...acc,
          [curr.name]: 0,
        }),
        {},
      ),
  )
  useEffect((): void => {
    setIssuerFields({})
  }, [issuerSchema])

  const filteredCountryOptions =
    selectedIssuer?.restrictions?.country !== undefined
      ? countryOptions.filter((opt) =>
          selectedIssuer.restrictions?.country.includes(opt.value),
        )
      : countryOptions

  const selectedCountry =
    filteredCountryOptions.length === 1
      ? Object.values(countrysMap).filter(
          (map) => map.iso === filteredCountryOptions[0].value,
        )[0]
      : accountAdditionalInfoPageFlatFields.country !== undefined
        ? countrysMap[accountAdditionalInfoPageFlatFields.country]
        : undefined
  const countrySchema =
    selectedCountry !== undefined ? selectedCountry.fields : undefined

  const countryFieldsValidation =
    (): FormFieldsValidation<RegisterAccountCountryFields> =>
      countrySchema !== undefined
        ? countrySchema.reduce((acc, curr) => {
            if (!curr.optional) {
              return { ...acc, [curr.name]: [stringIsEmpty()] }
            } else {
              return acc
            }
          }, {})
        : {}
  const {
    fields: countryFields,
    fieldsErrors: countryFieldsErrors,
    onFieldChange: onCountryFieldChange,
    setErrorsAndReturnDidPassValidation: countryFieldsSetErrors,
    topmostScrollGroup: countryFieldsTopmostScrollGroup,
  } = useFormFields<RegisterAccountCountryFields>(
    {},
    countryFieldsValidation,
    errorDetailMap,
    countrySchema?.reduce(
      (acc, curr) => ({
        ...acc,
        [errorPath(['createAccountRequest', curr.name])]: curr.name,
      }),
      {},
    ),
    countrySchema?.reduce(
      (
        acc: Partial<Record<keyof RegisterAccountCountryFields, number>>,
        curr,
      ) => ({
        ...acc,
        [curr.name]: 2,
      }),
      {},
    ),
  )

  const onAccountAdditionalInfoPageSubmit = (): void => {
    const accountAdditionalInfoPageFlatFieldsPassedValidation =
      accountAdditionalInfoPageFlatFieldsSetErrors()
    const issuerFieldsPassedValidation = issuerFieldsSetErrors()
    const countryFieldsPassedValidation = countryFieldsSetErrors()
    if (user?.organizationId) {
      onAccountAdditionalInfoPageFlatFieldChange(
        'organizationId',
        user?.organizationId,
      )
    }
    if (
      (accountAdditionalInfoPageFlatFieldsPassedValidation &&
        issuerFieldsPassedValidation &&
        countryFieldsPassedValidation) ||
      isJpm
    ) {
      analytics?.track('register account - billing info - button press', {
        intent: 'continue to account name',
        interaction: 'button press',
        screen: registerAccountUrl(),
      })

      setCurrentPage(3)
    }
  }

  const displayNameFlatFieldsValidation =
    (): FormFieldsValidation<AccountDisplayNameFlatFields> => ({
      displayName: [stringIsEmpty()],
    })
  const {
    fields: displayNameFlatFields,
    fieldsErrors: displayNameFlatFieldsErrors,
    onFieldChange: onDisplayNameFlatFieldChange,
    setErrorsAndReturnDidPassValidation: displayNameFlatFieldsSetErrors,
  } = useFormFields<AccountDisplayNameFlatFields>(
    {
      displayName: '',
    },
    displayNameFlatFieldsValidation,
    errorDetailMap,
    {
      'createAccountRequest.displayName': 'displayName',
    },
    {
      displayName: 0,
    },
  )

  const onDisplayNameSubmit = (): void => {
    const displayNameFlatFieldsPassedValidation =
      displayNameFlatFieldsSetErrors()
    if (displayNameFlatFieldsPassedValidation) {
      analytics?.track('register account - account name - button press', {
        intent: 'continue to review and confirm',
        interaction: 'button press',
        screen: registerAccountUrl(),
      })
      setCurrentPage(4)
    }
  }

  const transformedAccountAdditionalInfoPageFlatFields =
    (): AccountAdditionalInfoPageFlatFields => {
      const cardNumber =
        (accountBinInputPageFlatFields.cardNumber as string) ||
        (step1FlatFields?.cardNumber as string)
      const baseFields = {
        country: accountAdditionalInfoPageFlatFields.country,
        organizationId: accountAdditionalInfoPageFlatFields.organizationId,
      }

      const primaryOrSecondaryCardholder = (cardNumber: string): string => {
        const secondToLast = cardNumber[cardNumber.length - 2]
        const thirdToLast = cardNumber[cardNumber.length - 3]
        return secondToLast === '0' && thirdToLast === '0'
          ? 'primary'
          : 'secondary'
      }

      if (
        accountAdditionalInfoPageFlatFields.cardType &&
        accountAdditionalInfoPageFlatFields.cardType !==
          AmexCardTypeFields.not_sure
      ) {
        return {
          ...baseFields,
          amexCardProperties: {
            cardType: accountAdditionalInfoPageFlatFields.cardType,
            cardholderType:
              accountAdditionalInfoPageFlatFields.cardType === 'business'
                ? primaryOrSecondaryCardholder(cardNumber)
                : null,
          },
        }
      } else {
        return {
          ...baseFields,
        }
      }
    }

  const onPageThreeSubmit = (): void => {
    if (shouldShowSaasEligibilityLoader) return

    // assumes here that all FE validation is done already
    const ff = transformedAccountAdditionalInfoPageFlatFields()

    analytics?.track(
      'register account - review - confirm account registration',
      {
        intent: 'confirm account registration',
        interaction: 'button press',
        screen: registerAccountUrl(),
      },
    )

    // TODO: handle the conversion of "not_sure" card type to null or undefined
    const postCreditCardFields = {
      ...displayNameFlatFields,
      ...ff,
      ...countryFields,
      issuerId: selectedIssuer?.id,

      issuerFields: {
        ...issuerFields,
        ...(step1FlatFields.expirationDate !== undefined
          ? {
              expires: moment(
                step1FlatFields.expirationDate,
                getLocaleInfo(locale).shortDateFormat,
              )
                .endOf('month')
                .format('YYYY-MM-DD'),
            }
          : {}),
        ...(step1FlatFields.cardNumber !== undefined
          ? { cardNumber: step1FlatFields.cardNumber.replace(/\s+/g, '') }
          : {}),
        ...(step1CvcFlatFields.cvc !== undefined
          ? { cvc: step1CvcFlatFields.cvc.replace(/\s+/g, '') }
          : {}),
      },
    }
    const postJPMPaymentNetInfoFields = {
      ...countryFields,
      ...displayNameFlatFields,
      country: accountAdditionalInfoPageFlatFields.country,
      organizationId: accountAdditionalInfoPageFlatFields.organizationId,
      issuerId: selectedIssuer?.id,
      issuerFields: {
        ...issuerFields,
        ...step1FlatFieldsJpm,
      },
    }

    dispatch(
      postSecureCreditCardAndOpenToastAndCloseFlyout(
        isJpm ? postJPMPaymentNetInfoFields : postCreditCardFields,
        location.state,
        shouldRouteToPricingAfterCardRegistration,
        analytics,
      ),
    )
  }

  useEffect((): void => {
    if (Object.keys(accountBinInputPageFlatFieldsErrors).length > 0) {
      if (backgroundLocation.pathname === onboardingUrl()) {
        dispatch(closeModal())
      } else {
        setCurrentPage(1)
      }
    } else if (
      (Object.keys(accountAdditionalInfoPageFlatFieldsErrors).length > 0 ||
        Object.keys(issuerFieldsErrors).length > 0 ||
        Object.keys(countryFieldsErrors).length > 0) &&
      brand !== SupportedBrands.Jpm
    ) {
      setCurrentPage(2)
    } else if (Object.keys(displayNameFlatFieldsErrors).length > 0) {
      setCurrentPage(3)
    }
  }, [
    displayNameFlatFieldsErrors,
    accountAdditionalInfoPageFlatFieldsErrors,
    issuerFieldsErrors,
    countryFieldsErrors,
    accountBinInputPageFlatFieldsErrors,
  ])

  const closePopup = (): void => {
    // reset bin range info on close
    dispatch(createClearBinRangeInfoAction())

    const location = (): string => {
      switch (currentPage) {
        default:
        case 1:
          return 'card info'
        case 2:
          return 'billing info'
        case 3:
          return 'account name'
        case 4:
          return 'review'
      }
    }
    analytics?.track(`register account - ${location()} - exit`, {
      intent: 'exit register account flow',
      interaction: 'popup close',
      screen: registerAccountUrl(),
    })
  }

  return (
    <Popup
      noPadding
      css={css`
        display: flex;
        flex-direction: column;
        position: relative;
        ${hideCloseButton && 'width: 900px'}
      `}
      onClose={closePopup}
      registerAccount={{
        currentPage: currentPage,
      }}
      hideExit={
        backgroundLocation.pathname === onboardingUrl() || hideCloseButton
      }
    >
      <TestId testId={'registerAccountPopup'}>
        <Helmet>
          <title>{`Register an Account | ${appName}`}</title>
          <meta
            name="description"
            content={`Register an Account page of ${appName}`}
          />
        </Helmet>
        {!isJpm && (
          <img
            css={css`
              position: absolute;
              top: 0;
              right: 0;
              border-radius: 0px 10px 0px 0px;

              ${getBreakpointDown(breakpointSizes.XSM)(css`
                display: none;
              `)}
            `}
            src={Registration}
            alt={''}
          />
        )}
        {shouldShowSaasEligibilityLoader ? <LoadingBackgroundPage /> : null}
        {!shouldShowSaasEligibilityLoader && currentPage === 1 && (
          <div data-testid="register-account-step-1">
            {isJpm ? (
              <RegisterAccountPaymentNetinputForm
                flatFields={jpmFlatFields}
                flatFieldsErrors={jpmFlatFieldsErrors}
                onFlatFieldChange={onJpmFlatFieldChange}
                onSubmit={onPaymentNetInputFormSubmit}
                title={translateNode('Register an account')}
                subtitle={translateNode(
                  'Provide your J.P. Morgan PaymentNet information below to register an account. This account will be used to pay for expenses made with J.P. Morgan Virtual Cards.',
                )}
                cardInfoHeader={cardInfoHeader}
              />
            ) : (
              <RegisterAccountBinInputPage
                flatFields={accountBinInputPageFlatFields}
                onFlatFieldChange={onAccountBinInputPageFlatFieldChange}
                flatFieldsErrors={accountBinInputPageFlatFieldsErrors}
                cvcFlatFields={accountBinInputPageCvcFlatFields}
                onCvcFlatFieldChange={onAccountBinInputPageCvcFlatFieldChange}
                cvcFlatFieldsErrors={accountBinInputPageCvcFlatFieldsErrors}
                loading={fetchingIssuer}
                onSubmit={onAccountBinInputPageSubmit}
                hasOrganization={!!user?.organizationId}
                topLevelError={topLevelError}
              />
            )}
          </div>
        )}
        {!shouldShowSaasEligibilityLoader && currentPage === 2 && (
          <div data-testid="register-account-step-2">
            <RegisterAccountAdditionalInfoPage
              selectedIssuer={selectedIssuer}
              flatFields={accountAdditionalInfoPageFlatFields}
              onFlatFieldChange={onAccountAdditionalInfoPageFlatFieldChange}
              flatFieldsErrors={accountAdditionalInfoPageFlatFieldsErrors}
              flatFieldsTopmostScrollGroup={
                accountAdditionalInfoPageFlatFieldsTopmostScrollGroup
              }
              issuerFields={issuerFields}
              onIssuerFieldChange={onIssuerFieldChange}
              issuerFieldsErrors={issuerFieldsErrors}
              issuerFieldsTopmostScrollGroup={issuerFieldsTopmostScrollGroup}
              selectedCountry={selectedCountry}
              countryOptions={filteredCountryOptions}
              countryFields={countryFields}
              onCountryFieldChange={onCountryFieldChange}
              countryFieldsErrors={countryFieldsErrors}
              countryFieldsTopmostScrollGroup={countryFieldsTopmostScrollGroup}
              onAddressFormBack={() => {
                setHideCloseButton(false)
              }}
              onReviewCreateCardDetails={() => {
                setHideCloseButton(false)
              }}
              on3dsVerificationStart={() => {
                setHideCloseButton(true)
              }}
              on3dsVerificationError={() => {
                setHideCloseButton(false)
              }}
              onNavBack={(): void => {
                if (backgroundLocation.pathname === onboardingUrl()) {
                  dispatch(closeModal())
                } else {
                  analytics?.track('register account - billing info - back', {
                    intent: 'go back to card info modal',
                    interaction: 'button press',
                    screen: registerAccountUrl(),
                  })
                  astrada.reset()
                  setCurrentPage(1)
                }
              }}
              onSubmit={onAccountAdditionalInfoPageSubmit}
            />
          </div>
        )}
        {!shouldShowSaasEligibilityLoader && currentPage === 3 && (
          <div data-testid="register-account-step-3">
            <RegisterAccountDisplayNameContent
              flatFields={displayNameFlatFields}
              onFlatFieldChange={onDisplayNameFlatFieldChange}
              flatFieldsErrors={displayNameFlatFieldsErrors}
              onNavBack={(): void => {
                analytics?.track('register account - account name - back', {
                  intent: 'go back to billing info',
                  interaction: 'button press',
                  screen: registerAccountUrl(),
                })
                setCurrentPage(2)
              }}
              onSubmit={onDisplayNameSubmit}
              extraPadding
            />
          </div>
        )}
        {!shouldShowSaasEligibilityLoader && currentPage === 4 && (
          <div data-testid="register-account-step-4">
            <RegisterAccountReviewInformation
              displayNameFlatFields={displayNameFlatFields}
              accountBinInputPageFlatFields={step1FlatFields}
              accountBinInputPageCvcFlatFields={step1CvcFlatFields}
              paymentNetInputPageFlatFields={step1FlatFieldsJpm}
              selectedIssuer={selectedIssuer}
              issuerSchema={issuerSchema}
              issuerFields={issuerFields}
              selectedCountry={selectedCountry}
              countryFields={countryFields}
              accountAdditionalInfoPageFlatFields={
                accountAdditionalInfoPageFlatFields
              }
              returnToCardInformation={(): void => {
                analytics?.track(
                  'register account - review - update card info',
                  {
                    intent: 'edit card info',
                    interaction: 'button press',
                    screen: registerAccountUrl(),
                  },
                )
                setCurrentPage(1)
              }}
              returnToAdditionalInformation={(): void => {
                analytics?.track(
                  'register account - review - update billing info',
                  {
                    intent: 'edit billing or additional info',
                    interaction: 'button press',
                    screen: registerAccountUrl(),
                  },
                )
                setCurrentPage(2)
              }}
              returnToDisplayName={(): void => {
                setCurrentPage(3)
              }}
              postingCreditCard={postingCreditCard}
              topLevelError={topLevelError}
              onSubmit={onPageThreeSubmit}
              extraPadding
            />
          </div>
        )}
      </TestId>
    </Popup>
  )
}

export default RegisterAccount
